import pino from "pino";
import { PINO_LEVELS, type PinoLevelName, pinoLevelName } from "@egalregal/shared";

type LogLevel = PinoLevelName | "silent";

const TRANSMIT_LEVELS: Record<LogLevel, number> = {
  ...PINO_LEVELS,
  silent: Infinity,
};

interface WindowWithLogLevel extends Window {
  UI_LOG_LEVEL?: string;
}

function explicitLogLevel(): LogLevel | undefined {
  if (typeof window !== "undefined" && (window as WindowWithLogLevel).UI_LOG_LEVEL) {
    return (window as WindowWithLogLevel).UI_LOG_LEVEL as LogLevel;
  }
  return undefined;
}

function readLogLevel(): LogLevel {
  return explicitLogLevel() ?? "debug";
}

function hasExplicitUILogLevel(): boolean {
  return Boolean(explicitLogLevel());
}

/**
 * Transmit gate: only when UI_LOG_LEVEL is set, at or above that level.
 *
 * @param levelValue the numeric pino level of the log event.
 * @returns true when the event should be sent to /api/logs via sendBeacon.
 */
export function shouldTransmitLevel(levelValue: number): boolean {
  if (!hasExplicitUILogLevel()) return false;
  return levelValue >= TRANSMIT_LEVELS[readLogLevel()];
}

/**
 * Boundaries for the Error-aware serializer below: recursion depth and the
 * longest stack string transmitted. Both keep the `sendBeacon` transmit path
 * from hanging or ballooning on circular structures and huge stacks.
 */
const MAX_SERIALIZE_DEPTH = 10;
const MAX_STACK_LENGTH = 8000;

/**
 * Serialize a log value into a JSON-safe shape, preserving `Error` detail.
 *
 * `Error` message/stack are non-enumerable, so plain `JSON.stringify` drops
 * them (`{}`). Errors become `{ name, message, stack, cause? }` plus any extra
 * enumerable own props; the `cause` chain is followed recursively. Plain
 * objects and arrays are walked recursively; primitives pass through
 * untouched. Circular references become `"[Circular]"` (ancestor-scoped, so a
 * shared object referenced twice non-circularly still serializes fully both
 * times), over-deep nesting `"[MaxDepth]"`, and getters that throw
 * `"[Unserializable]"`.
 */
export function serializeLogValue(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (value instanceof Error) {
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    try {
      const out: Record<string, unknown> = {
        name: value.name,
        message: value.message,
      };
      if (typeof value.stack === "string") {
        out.stack =
          value.stack.length > MAX_STACK_LENGTH
            ? value.stack.slice(0, MAX_STACK_LENGTH) + "…[truncated]"
            : value.stack;
      }
      let cause: unknown;
      try {
        cause = (value as { cause?: unknown }).cause;
      } catch {
        cause = "[Unserializable]";
      }
      if (cause !== undefined) {
        out.cause =
          depth >= MAX_SERIALIZE_DEPTH || typeof cause === "string"
            ? cause
            : serializeLogValue(cause, depth + 1, seen);
      }
      for (const key of Object.keys(value)) {
        if (key === "name" || key === "message" || key === "stack" || key === "cause") continue;
        try {
          out[key] =
            depth >= MAX_SERIALIZE_DEPTH
              ? "[MaxDepth]"
              : serializeLogValue((value as unknown as Record<string, unknown>)[key], depth + 1, seen);
        } catch {
          out[key] = "[Unserializable]";
        }
      }
      return out;
    } finally {
      seen.delete(value);
    }
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) return "[Circular]";
    if (depth >= MAX_SERIALIZE_DEPTH) return "[MaxDepth]";
    seen.add(value);
    try {
      return value.map((entry) => serializeLogValue(entry, depth + 1, seen));
    } finally {
      seen.delete(value);
    }
  }
  if (typeof value === "object" && value !== null) {
    if (seen.has(value)) return "[Circular]";
    if (depth >= MAX_SERIALIZE_DEPTH) return "[MaxDepth]";
    seen.add(value);
    try {
      const out: Record<string, unknown> = {};
      // Object.keys (not entries): key listing never invokes getters, and each
      // per-key read below is guarded, so a throwing getter degrades to a
      // "[Unserializable]" marker instead of crashing the transmit path.
      for (const key of Object.keys(value)) {
        try {
          out[key] = serializeLogValue(
            (value as Record<string, unknown>)[key],
            depth + 1,
            seen,
          );
        } catch {
          out[key] = "[Unserializable]";
        }
      }
      return out;
    } finally {
      seen.delete(value);
    }
  }
  return value;
}

/** `JSON.stringify` that never throws — falls back to a marker string. */
function safeStringify(value: unknown): string {
  try {
    const json = JSON.stringify(value);
    return typeof json === "string" ? json : "[Unserializable]";
  } catch {
    return "[Unserializable]";
  }
}

/**
 * Flatten a pino browser LogEvent into the JSON envelope sent to the server:
 * the level as its name, an ISO timestamp, the joined message and any
 * bindings. Message args and bindings go through `serializeLogValue` first so
 * `Error` detail (name/message/stack/cause) survives the transmit instead of
 * collapsing to `{}`.
 */
export function flattenLogEvent(logEvent: pino.LogEvent): Record<string, unknown> {
  const levelValue = logEvent.level?.value ?? PINO_LEVELS.info;
  const timestamp = typeof logEvent.ts === "number" ? new Date(logEvent.ts).toISOString() : new Date().toISOString();

  const messages = Array.isArray(logEvent.messages) ? logEvent.messages : [];
  const msg = messages.length > 0 ? messages.map((entry) => typeof entry === "string" ? entry : safeStringify(serializeLogValue(entry))).join(" ") : "frontend log";

  const mergedBindings = (Array.isArray(logEvent.bindings) ? logEvent.bindings : [])
    .filter((binding): binding is Record<string, unknown> => typeof binding === "object" && binding !== null)
    .reduce<Record<string, unknown>>((acc, binding) => Object.assign(acc, binding), {});
  const bindings = serializeLogValue(mergedBindings) as Record<string, unknown>;

  return {
    level: pinoLevelName(levelValue),
    time: timestamp,
    msg,
    ...bindings,
  };
}

const logger = pino({
  browser: {
    asObject: true,
    transmit: {
      send: (level: string, logEvent: pino.LogEvent, _val?: number) => {
        const levelValue = logEvent.level?.value ?? PINO_LEVELS.info;
        if (!shouldTransmitLevel(levelValue)) return;

        const body = JSON.stringify(flattenLogEvent(logEvent));
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
          navigator.sendBeacon("/api/logs", body);
        }
      },
    },
  },
});

logger.level = readLogLevel();

export function setFrontendLogLevel(level: LogLevel): void {
  logger.level = level;
}

export function getFrontendLogLevel(): LogLevel {
  return readLogLevel();
}

export const log = logger;
export default logger;